import React, { PropsWithChildren, useState } from "react";

import { Paragraph, Small } from "@fronterahealth/frontera-ui-components";
import {
  NestedOpenDirection,
  PopoverMenu,
  PopoverMenuItem,
} from "@fronterahealth/frontera-ui-components/PopoverActionMenu/PopoverActionMenu";

import { ReportGoalType, useRemoveReportGoalMutation } from "@api/graphql/types-and-hooks";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

interface ShortTermGoalCardProps extends PropsWithChildren {
  onClick: () => void;
  goalObject: ReportGoalType;
  refetchGoals: () => Promise<void>;
  onSaveToFolder?: () => void;
  uiGoalId?: string;
  submenuGoalsList: PopoverMenuItem[];
  submenuGoalsOpenDirection?: NestedOpenDirection;
}

export const ShortTermGoalCard: React.FC<ShortTermGoalCardProps> = ({
  goalObject,
  onClick,
  children,
  refetchGoals,
  onSaveToFolder,
  uiGoalId,
  submenuGoalsList,
  submenuGoalsOpenDirection = "right",
}) => {
  const removeReportGoalMutation = useRemoveReportGoalMutation({});
  const [deleteLoading, setDeleteLoading] = useState(false);

  return (
    <div data-testid="short-term-goal-card">
      <div
        onClick={onClick}
        className="flex flex-col h-full hover:cursor-pointer border-border-medium hover:border-border-focus border transition-all rounded-md bg-white"
      >
        {/* Header section with fixed height */}
        <div className="flex items-center p-4 shrink-0">
          <div className="w-7 shrink-0">
            <Paragraph displayType="loud" colorType="secondary">
              {uiGoalId ?? null}
            </Paragraph>
          </div>
          <div className="grow overflow-hidden ph-no-capture">
            <Paragraph displayType="loud" colorType="primary" className="truncate">
              {goalObject?.goalName}
            </Paragraph>
          </div>
          <div className="w-6 shrink-0 ml-2">
            <PopoverMenu
              openDirection="to-bottom-left"
              submenuOpenDirection={submenuGoalsOpenDirection}
              menuItems={[
                {
                  label: "Edit",
                  callback: () => onClick(),
                },
                {
                  label: "Save to Folder...",
                  callback: () => {
                    if (onSaveToFolder) onSaveToFolder();
                  },
                },
                {
                  label: "Move to ",
                  submenu: submenuGoalsList,
                },
                {
                  label: deleteLoading ? "Deleting..." : "Delete",
                  callback: async () => {
                    setDeleteLoading(true);
                    try {
                      await removeReportGoalMutation.mutateAsync({
                        reportGoalId: goalObject.id,
                      });
                      notifySuccess("Successfully Deleted Short Term Goal");
                      await refetchGoals();
                      setDeleteLoading(false);
                    } catch (err) {
                      console.error("Failed To Delete Short Term Goal", err);
                      notifyError("Failed To Delete Short Term Goal");
                    }
                  },
                },
              ]}
            />
          </div>
        </div>

        {/* Description section with scrollable content */}
        <div className="p-4 pt-0 ph-no-capture">
          <Small displayType="normal" className="h-full overflow-y-auto">
            {goalObject.masteryCriteria}
          </Small>
        </div>

        {/* Footer section for children */}
        <div className="shrink-0 p-4 pt-0">{children}</div>
      </div>
    </div>
  );
};
